import { create } from 'zustand';
import i18next from 'i18next';

/** Translated when i18n is running (the app), the English default otherwise (tests). */
function tr(key: string, fallback: string, vars?: Record<string, unknown>): string {
  return i18next.isInitialized ? i18next.t(key, { defaultValue: fallback, ...vars }) : fallback.replace(/\{\{(\w+)\}\}/g, (_, k: string) => String(vars?.[k] ?? ''));
}

// ── Types ────────────────────────────────────────────────────
export type PresenceStatus = 'available' | 'busy' | 'away' | 'dnd' | 'offline';
export type WorkLocation = 'home' | 'office' | 'offsite';

export interface PresenceInfo {
  status: PresenceStatus;
  workLocation: string | null;
  customMessage: string | null;
  lastSeen: string;
}

// ── Store ────────────────────────────────────────────────────
interface PresenceState {
  /** Map of userId → their current presence */
  presenceMap: Record<string, PresenceInfo>;
  /** Current user's own status */
  myStatus: PresenceStatus;
  /** Current user's work location */
  myWorkLocation: WorkLocation;
  /** WebSocket reference shared across components */
  ws: WebSocket | null;

  // Setters
  setPresence: (userId: string, info: PresenceInfo) => void;
  setBulkPresence: (map: Record<string, PresenceInfo>) => void;
  setMyStatus: (status: PresenceStatus) => void;
  setMyWorkLocation: (location: WorkLocation) => void;
  setWs: (ws: WebSocket | null) => void;

  // WebSocket senders
  sendPresenceUpdate: (status: PresenceStatus, workLocation?: WorkLocation) => void;
  sendPresenceSubscribe: (userIds: string[]) => void;
  sendPresenceUnsubscribe: (userIds: string[]) => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  presenceMap: {},
  myStatus: 'available',
  myWorkLocation: 'home',
  ws: null,

  setPresence: (userId, info) =>
    set((state) => ({ presenceMap: { ...state.presenceMap, [userId]: info } })),

  setBulkPresence: (map) =>
    set((state) => ({ presenceMap: { ...state.presenceMap, ...map } })),

  setMyStatus: (status) => set({ myStatus: status }),
  setMyWorkLocation: (location) => set({ myWorkLocation: location }),
  setWs: (ws) => set({ ws }),

  sendPresenceUpdate: (status, workLocation) => {
    const ws = get().ws;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'presence.update',
        // backend uses 'online' — frontend uses 'available'
        status: status === 'available' ? 'online' : status,
        workLocation: workLocation ?? get().myWorkLocation,
      }));
    }
    set({ myStatus: status });
  },

  sendPresenceSubscribe: (userIds) => {
    const ws = get().ws;
    if (ws?.readyState === WebSocket.OPEN && userIds.length > 0) {
      ws.send(JSON.stringify({ type: 'presence.subscribe', userIds }));
    }
  },

  sendPresenceUnsubscribe: (userIds) => {
    const ws = get().ws;
    if (ws?.readyState === WebSocket.OPEN && userIds.length > 0) {
      ws.send(JSON.stringify({ type: 'presence.unsubscribe', userIds }));
    }
  },
}));

// ── Utility helpers ──────────────────────────────────────────

/** Map a backend status string to a PresenceStatus */
export function toPresenceStatus(s: string): PresenceStatus {
  if (s === 'online') return 'available';
  if (['available', 'busy', 'away', 'dnd', 'offline'].includes(s)) return s as PresenceStatus;
  return 'offline';
}

/** CSS class for a presence dot colour (Nord / Aurora palette) */
export function presenceDotClass(status: PresenceStatus | undefined): string {
  switch (status) {
    case 'available': return 'bg-aurora-3';
    case 'busy':      return 'bg-nord-11';
    case 'away':      return 'bg-aurora-2';
    case 'dnd':       return 'bg-nord-12';
    case 'offline':
    default:          return 'bg-nord-3';
  }
}

/** Accessible text label for presence status */
export function presenceLabel(status: PresenceStatus | undefined): string {
  switch (status) {
    case 'available': return tr('presence.available', 'Available');
    case 'busy':      return tr('presence.busy', 'Busy');
    case 'away':      return tr('presence.away', 'Away');
    case 'dnd':       return tr('presence.dnd', 'Do not disturb');
    case 'offline':
    default:          return tr('presence.offline', 'Offline');
  }
}

/** Unicode shape indicator for status (WCAG: not colour alone) */
export function presenceShape(status: PresenceStatus | undefined): string {
  switch (status) {
    case 'available': return '●';   // filled circle
    case 'busy':      return '⊘';   // circle with bar
    case 'away':      return '◐';   // half circle (clock-like)
    case 'dnd':       return '⊖';   // circle with minus
    case 'offline':
    default:          return '○';   // empty circle
  }
}

/** Human-readable relative time for lastSeen ("2m ago", "3h ago", …) */
export function relativeLastSeen(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return tr('presence.justNow', 'just now');
  if (diff < 3_600_000) return tr('presence.minutesAgo', '{{n}}m ago', { n: Math.floor(diff / 60_000) });
  if (diff < 86_400_000) return tr('presence.hoursAgo', '{{n}}h ago', { n: Math.floor(diff / 3_600_000) });
  return tr('presence.daysAgo', '{{n}}d ago', { n: Math.floor(diff / 86_400_000) });
}
